// Main App component with routing
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CompetitionProvider } from './contexts/CompetitionContext';
import { LeagueProvider } from './contexts/LeagueContext';

// Screens
import Home from './screens/Home';
import CreateCompetition from './screens/CreateCompetition';
import JoinCompetition from './screens/JoinCompetition';
import CreateAccount from './screens/CreateAccount';
import Dashboard from './screens/Dashboard';
import CategoryView from './screens/CategoryView';
import GameDetails from './screens/GameDetails';
import AdminPanel from './screens/AdminPanel';
import Profile from './screens/Profile';
import PlayerProfile from './screens/PlayerProfile';
import HallOfFame from './screens/HallOfFame';
import GameLog from './screens/GameLog';
import GameVoting from './screens/GameVoting';
import WizardScoreboard from './screens/WizardScoreboard';

// League Screens
import LeagueList from './screens/LeagueList';
import LeagueDashboard from './screens/LeagueDashboard';
import LeagueCreateCompetition from './screens/LeagueCreateCompetition';
import JoinLeague from './screens/JoinLeague';
import GameSectionManager from './screens/GameSectionManager';
import MigrationTool from './screens/MigrationTool';
import DebugCompetition from './screens/DebugCompetition';
import SuperAdmin from './screens/SuperAdmin';

function App() {
  return (
    <AuthProvider>
      <LeagueProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Home />} />
            <Route path="/create" element={<CreateCompetition />} />
            <Route path="/join" element={<JoinCompetition />} />
            <Route path="/create-account" element={<CreateAccount />} />

            {/* League routes */}
            <Route path="/leagues" element={<LeagueList />} />
            <Route path="/league/:leagueId" element={<LeagueDashboard />} />
            <Route path="/league/:leagueId/create-competition" element={<LeagueCreateCompetition />} />
            <Route path="/league/:leagueId/games" element={<GameSectionManager />} />
            <Route path="/league/:leagueId/hall-of-fame" element={<HallOfFame />} />
            <Route path="/join-league/:inviteId" element={<JoinLeague />} />
            <Route path="/migrate" element={<MigrationTool />} />
            <Route path="/debug/:competitionId" element={<DebugCompetition />} />
            <Route path="/superadmin" element={<SuperAdmin />} />

          {/* Competition routes */}
          <Route
            path="/competition/:competitionId"
            element={
              <CompetitionRoute>
                <Dashboard />
              </CompetitionRoute>
            }
          />
          <Route
            path="/competition/:competitionId/category/:categoryId"
            element={
              <CompetitionRoute>
                <CategoryView />
              </CompetitionRoute>
            }
          />
          <Route
            path="/competition/:competitionId/game/:gameId"
            element={
              <CompetitionRoute>
                <GameDetails />
              </CompetitionRoute>
            }
          />
          <Route
            path="/competition/:competitionId/admin"
            element={
              <CompetitionRoute>
                <AdminPanel />
              </CompetitionRoute>
            }
          />
          <Route
            path="/competition/:competitionId/profile"
            element={
              <CompetitionRoute>
                <Profile />
              </CompetitionRoute>
            }
          />
          <Route
            path="/competition/:competitionId/player/:playerId"
            element={
              <CompetitionRoute>
                <PlayerProfile />
              </CompetitionRoute>
            }
          />
          <Route
            path="/competition/:competitionId/game-log"
            element={
              <CompetitionRoute>
                <GameLog />
              </CompetitionRoute>
            }
          />
          <Route
            path="/competition/:competitionId/voting"
            element={
              <CompetitionRoute>
                <GameVoting />
              </CompetitionRoute>
            }
          />
          <Route
            path="/competition/:competitionId/game/:gameId/wizard"
            element={
              <CompetitionRoute>
                <WizardScoreboard />
              </CompetitionRoute>
            }
          />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </LeagueProvider>
    </AuthProvider>
  );
}

// Helper component to extract competitionId from route params
const CompetitionRoute = ({ children }) => {
  const { competitionId } = useParams();

  return (
    <CompetitionProvider competitionId={competitionId}>
      {children}
    </CompetitionProvider>
  );
};

export default App;
